import React, { FC } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Box, Container, Grid, Theme, Typography } from '@material-ui/core';
import { TextContentProps } from 'components/energy5/structures/TextContent';
import { useMediaQueries } from 'hooks';
import Energy5Button from 'components/energy5/atoms/Energy5Button';
import FaqToggleAccordion from 'containers/common/sections/faq/FaqToggleAccordion';
import { Faq } from 'services/cms/types';

interface ServiceFAQsProps {
  title: string;
  text: string;
  imageSrc: string;
  imageRight?: boolean;
  faqs: Faq[];
  onToggleAccordion: () => void;
  open: boolean;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      padding: theme.spacing(10, 0, 0),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(14, 0, 0),
      },
    },
    textBox: {
      marginBottom: theme.spacing(8),
      [theme.breakpoints.up('md')]: {
        margin: theme.spacing(0),
      },
    },
    exploreImg: {
      width: '100%',
      borderRadius: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        maxHeight: 400,
        objectFit: 'cover',
      },
    },
    container: {
      flexDirection: ({ imageRight }: Partial<ServiceFAQsProps>) =>
        imageRight ? 'row-reverse' : 'row',
      alignItems: 'flex-start',
    },
    textContentWrapper: {
      [theme.breakpoints.up('md')]: {
        paddingTop: theme.spacing(0),
      },
      [theme.breakpoints.up('lg')]: {
        paddingTop: theme.spacing(6),
      },
    },
    title: {
      borderLeft: `3px solid ${theme.palette.customColors.light}`,
      paddingLeft: theme.spacing(3),
      color: ({ enableDarkMode }: Partial<TextContentProps>) =>
        enableDarkMode ? theme.palette.common.white : theme.palette.secondary.main,
      textTransform: 'uppercase',
      [theme.breakpoints.up('sm')]: {
        textTransform: 'none',
      },
    },
    text: {
      color: ({ enableDarkMode }: Partial<TextContentProps>) =>
        enableDarkMode ? theme.palette.customColors.lightGray : theme.palette.text.primary,
      whiteSpace: 'pre-line',
      margin: theme.spacing(4, 0, 6),
    },
  };
});

const ServiceFAQs: FC<ServiceFAQsProps> = (props) => {
  const { title, imageSrc, text, imageRight = false, faqs, open, onToggleAccordion } = props;
  const classes = useStyles({ imageRight });
  const isMediumUp = useMediaQueries((theme: Theme) => theme.breakpoints.up('md'));
  const isLargeUp = useMediaQueries((theme: Theme) => theme.breakpoints.up('lg'));

  return (
    <Box className={classes.root}>
      <Container maxWidth={false} fixed>
        <Grid
          container
          justifyContent="space-between"
          spacing={isMediumUp ? 6 : 8}
          className={classes.container}
        >
          <Grid item lg={6} md={6} xs={12}>
            <Box style={{ position: 'relative' }}>
              <img src={imageSrc} alt={title} className={classes.exploreImg} />
            </Box>
          </Grid>
          <Grid item lg={5} md={6} xs={12} className={classes.textBox}>
            <Box className={classes.textContentWrapper} mt={isLargeUp && open ? -5 : 8}>
              <Box>
                <Typography variant="h3" className={classes.title}>
                  {title}
                </Typography>
              </Box>
              <Typography variant="h6" className={classes.text}>
                {text}
              </Typography>
              <Energy5Button text="Explore FAQ" onClick={onToggleAccordion} />
            </Box>
          </Grid>
        </Grid>
      </Container>
      {open && <FaqToggleAccordion faqs={faqs} />}
    </Box>
  );
};

export default ServiceFAQs;
